import { useState } from 'react';

const SubmissionForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    submissionType: 'Artist Submission',
    streamingLink: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('submissionType', formData.submissionType);
      formBody.append('streamingLink', formData.streamingLink);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d621ipc7f73jei4pha1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          submissionType: 'Artist Submission',
          streamingLink: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="bg-cream py-16 sm:py-32">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="font-display text-2xl sm:text-4xl md:text-5xl font-black text-leather-dark mb-4 sm:mb-6 uppercase">
            Get in touch
          </h2>
          <p className="font-sans text-sm sm:text-lg text-leather-dark/70 leading-relaxed">
            Send streaming links + short bio to <strong className="font-semibold text-orange-burnt">info@ranchfireradio.com</strong>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="space-y-4 sm:space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block font-sans text-sm font-bold text-leather-dark mb-2 uppercase tracking-wider">
              Name / Artist Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              maxLength={100}
              className="w-full bg-transparent border-2 border-leather-dark/30 py-4 px-4 text-leather-dark placeholder-leather-dark/50 focus:outline-none focus:border-orange-burnt transition-colors font-sans text-sm"
              placeholder="Your name"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block font-sans text-sm font-bold text-leather-dark mb-2 uppercase tracking-wider">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              maxLength={100}
              className="w-full bg-transparent border-2 border-leather-dark/30 py-4 px-4 text-leather-dark placeholder-leather-dark/50 focus:outline-none focus:border-orange-burnt transition-colors font-sans text-sm"
              placeholder="your@email.com"
            />
          </div>

          {/* Submission Type */}
          <div>
            <label htmlFor="submissionType" className="block font-sans text-sm font-bold text-leather-dark mb-2 uppercase tracking-wider">
              Submission Type
            </label>
            <select
              id="submissionType"
              name="submissionType"
              value={formData.submissionType}
              onChange={handleChange}
              required
              className="w-full bg-transparent border-2 border-leather-dark/30 py-4 px-4 text-leather-dark focus:outline-none focus:border-orange-burnt transition-colors font-sans text-sm cursor-pointer"
            >
              <option value="Artist Submission">Artist Submission</option>
              <option value="Management Inquiry">Management Inquiry</option>
              <option value="Sync Licensing">Sync Licensing</option>
              <option value="Publishing">Publishing</option>
              <option value="General Inquiry">General Inquiry</option>
            </select>
          </div>

          {/* Streaming Link */}
          <div>
            <label htmlFor="streamingLink" className="block font-sans text-sm font-bold text-leather-dark mb-2 uppercase tracking-wider">
              Streaming Link
            </label>
            <input
              type="url"
              id="streamingLink"
              name="streamingLink"
              value={formData.streamingLink}
              onChange={handleChange}
              maxLength={200}
              className="w-full bg-transparent border-2 border-leather-dark/30 py-4 px-4 text-leather-dark placeholder-leather-dark/50 focus:outline-none focus:border-orange-burnt transition-colors font-sans text-sm"
              placeholder="Spotify, Apple Music, SoundCloud, etc."
            />
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block font-sans text-sm font-bold text-leather-dark mb-2 uppercase tracking-wider">
              Message / Bio
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              maxLength={500}
              rows={6}
              className="w-full bg-transparent border-2 border-leather-dark/30 py-4 px-4 text-leather-dark placeholder-leather-dark/50 focus:outline-none focus:border-orange-burnt transition-colors font-sans text-sm resize-none"
              placeholder="Tell us about your music, background, and what you're looking for..."
            />
            <p className="text-xs text-leather-dark/50 mt-2 font-sans">Maximum 500 characters</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-orange-burnt text-cream px-12 py-5 rounded-none font-sans text-base font-bold uppercase tracking-wider hover:bg-leather-dark hover:border-leather-dark transition-all whitespace-nowrap cursor-pointer border-2 border-orange-burnt disabled:opacity-50"
          >
            {isSubmitting ? 'Sending...' : 'Submit'}
          </button>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-orange-burnt/10 border-2 border-orange-burnt p-6 text-center">
              <p className="text-orange-burnt font-sans text-sm font-bold uppercase tracking-wide">
                Thanks for reaching out! We'll be in touch soon.
              </p>
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="bg-red-100 border-2 border-red-400 p-6 text-center">
              <p className="text-red-600 font-sans text-sm font-bold uppercase tracking-wide">
                Something went wrong. Please try again or email us directly.
              </p>
            </div>
          )}
        </form>

        {/* Direct Contact */}
        <div className="mt-16 text-center">
          <p className="font-sans text-base text-leather-dark/70 mb-4">
            Or reach us directly:
          </p>
          <a
            href="mailto:info@ranchfireradio.com"
            className="font-sans text-xl text-orange-burnt hover:text-leather-dark transition-colors cursor-pointer font-bold"
          >
            info@ranchfireradio.com
          </a>
        </div>
      </div>
    </section>
  );
};

export default SubmissionForm;
